<template>
  <v-layout column>
    <v-flex mt-3>
      <v-card class="pa-3">
        <v-row>
          <v-col cols="12" class="pl-0">
            <attribute-filter-panel
              :filter-rules="architectureRulesLinePricingFilter"
              title-localisation="attributes.filters.filterByAttributes"
              filter-count-localisation="attributes.filters.numApplied"
              :fixed-filter-rules="fixedFilterRules"
              :enable-store-group-chain-filter="false"
              @attributeFilterChange="setAttributeFilter"
            />
          </v-col>
        </v-row>

        <div class="d-flex align-center mb-2">
          <norm-weight-toggle v-if="!isChainsModeEnabled" class="mr-2 d-inline-flex" />
          <line-pricing-import-button :disabled="isLoading" @click="handleImportClick" />
          <span>
            <line-pricing-export-button />
          </span>
          <v-spacer />
          <div class="d-flex align-center filter-buttons-container">
            <span class="filter-by-text mr-2">{{ $t('inputs.filterBy') }}</span>
            <v-btn
              class="columns-toggle grid-btn pa-0 mr-2"
              secondary
              small
              depressed
              @click="toggleSideBar('columns')"
            >
              <v-icon class="mr-1">mdi-view-column-outline</v-icon>
              {{ $t('inputs.columns') }}
            </v-btn>
            <v-btn
              class="filters-toggle grid-btn pa-0"
              secondary
              small
              depressed
              @click="toggleSideBar('filters')"
            >
              <v-icon class="mr-1">mdi-filter-outline</v-icon>
              {{ $t('inputs.filters') }}
            </v-btn>
          </div>
        </div>

        <div class="ag-grid-box">
          <ag-grid-vue
            style="width: 100%; height: 100%;"
            class="ag-theme-custom ag-theme-custom-stripped"
            :grid-options="gridOptions"
            :column-defs="columnDefs"
            :row-data="rowData"
            :default-col-def="defaultColDef"
            :suppress-row-click-selection="true"
            :stop-editing-when-cells-lose-focus="true"
            :fill-operation="fillOperation"
            :side-bar="sideBar"
            @grid-ready="onGridReady"
            @first-data-rendered="onFirstDataRendered"
            @cell-value-changed="onCellValueChanged"
            @column-visible="onColumnStateChanged"
            @column-pinned="onColumnStateChanged"
            @column-moved="onColumnStateChanged"
            @column-resized="onColumnStateChanged"
            @sort-changed="onColumnStateChanged"
          />
        </div>

        <div class="mt-3 text-right">
          <v-alert
            v-if="hasInvalidLinePricingAssignments && !isLoading"
            color="error"
            :icon="false"
            dense
            outlined
            elevation="0"
            class="mr-3 d-inline-flex mb-0 alert-message"
            role="alert"
          >
            {{ $t('settings.architecture.differentSubGroupValidationError') }}
          </v-alert>
          <v-btn
            class="grid-btn mr-2"
            secondary
            small
            :disabled="!Object.keys(dirtyRowsByKey).length"
            depressed
            @click="onDiscard"
          >
            {{ $t('inputs.cancel') }}
          </v-btn>
          <v-btn
            color="success"
            small
            :disabled="isSaveDisabled"
            :loading="isSaving"
            depressed
            @click="onSave"
          >
            {{ $t('actions.save') }}
          </v-btn>
        </div>
      </v-card>
    </v-flex>
    <line-pricing-groups-upload-data
      :show-dialog="showLPGUpload"
      :params="{
        architectureGroupId: importByArchitectureGroup ? architectureGroupId : null,
      }"
      @closeDialog="showLPGUpload = false"
    />
    <line-pricing-group-dialog
      v-model="showLpgDialog"
      :mode="lpgDialogMode"
      :groups="linePricingGroupOptions"
      :submitting="lpgDialogSubmitting"
      :success="lpgDialogSuccess"
      @create="onDialogCreate"
      @edit="onDialogEdit"
      @delete="onDialogDelete"
      @cancel="onCloseLpgDialog"
      @dismiss="onDismissLpgDialog"
    />
  </v-layout>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { get, find, isEqual, groupBy, map, sortBy, cloneDeep } from 'lodash';
import { AgGridVue } from 'ag-grid-vue';
import NormWeightToggle from '../../../../components/norm-weight-toggle.vue';
import featureFlagsMixin from '../../../../mixins/featureFlags';
import getPriceFieldMixin from '../../../../mixins/getPriceField';
import hierarchyFiltersMixin from '../../../../mixins/hierarchyFilters';
import formatNumberMixin from '../../../../mixins/formatNumber';
import sideBarMixin from '../../../../mixins/sideBar';
import numberFormats from '@enums/number-formats';
import agGridUtils from '../../../../utils/ag-grid-utils';
import {
  buildHideShowColumnState,
  extractColMetaMapFromColumns,
  handleLinePricingGroupFillOperation,
  handleLinePricingGroupValueChange,
} from '../../../../utils/line-pricing-column-helpers';
import {
  useZones,
  useStoreGroupChains,
  displayDistributionByAssortmentRole,
} from '@enums/feature-flags';
import { storegroupOption } from '@enums/filter-options';
import assortmentRoles from '@enums/assortment-roles';
import LinePricingGroupColumnHeader from './line-pricing-group-column-header.vue';
import LinePricingGroupDialog from './line-pricing-group-dialog.vue';

export default {
  name: 'LinePricingEditor',
  components: {
    NormWeightToggle,
    AgGridVue,
    LinePricingGroupDialog,
    // eslint-disable-next-line vue/no-unused-components
    LinePricingGroupColumnHeader,
  },
  mixins: [
    featureFlagsMixin,
    getPriceFieldMixin,
    hierarchyFiltersMixin,
    formatNumberMixin,
    sideBarMixin,
  ],
  props: {
    scenarioKey: {
      type: String,
      required: true,
    },
    architectureGroupId: {
      type: String,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      gridOptions: null,
      gridApi: null,
      columnApi: null,
      columnDefs: [],
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMovable: true,
        editable: false,
      },
      rowData: null, // Start with null to prevent "no rows to show" before loading
      dirtyRowsByKey: {},
      isSaving: false,
      hasLoadedData: false, // Track if data has been loaded at least once
      numberFormats,
      useZones,
      useStoreGroupChains,
      showLPGUpload: false,
      importByArchitectureGroup: false,
      // Populated from products; used to build dynamic per-store-group columns when chains are enabled
      storeGroupKeys: [],
      showLpgDialog: false,
      lpgDialogMode: 'create',
      lpgDialogSubmitting: false,
      lpgDialogSuccess: false,
      currentTsgKey: null,
      originalRowsSnapshot: null,
      invalidLpgKeys: new Set(),
    };
  },

  computed: {
    ...mapState('storeGroupRelationships', ['storeGroupChains', 'storegroupKeyNameMap']),
    ...mapState('clientConfig', ['releaseFlags']),
    ...mapState('architectureGroup', [
      'loading',
      'linePricingProductsLoading',
      'downloadingLinePricing',
    ]),
    ...mapState('architectureGroup', ['linePricingEditorColumnState']),
    ...mapGetters('architectureGroup', ['getLinePricingEditorColumnState']),
    ...mapGetters('architectureGroup', ['linePricingArchitectureProducts']),
    ...mapGetters('storeGroupRelationships', ['mainToolStoreGroupKeys']),
    ...mapState('architectureGroup', ['linePricingGroups']),
    ...mapState('filters', ['architectureRulesLinePricingFilter', 'selectedPriceType']),
    // Used to control grid loading overlay
    isLoading() {
      return this.loading || this.linePricingProductsLoading;
    },
    groupSelectValues() {
      const groups = (this.linePricingGroups || []).map(g => ({
        value: get(g, 'linePricingGroupKey'),
        label: get(g, 'linePricedGroupDescription') || get(g, 'linePricingGroupKey'),
      }));

      return groups.sort((a, b) => a.label.localeCompare(b.label));
    },
    isSaveDisabled() {
      if (this.disabled || !this.editable) return true;

      const dirty = Object.values(this.dirtyRowsByKey);
      if (!dirty.length) return true;

      const hasInvalid = dirty.some(
        r => r.linePricingGroupKey && (!r.linePriceFactor || r.linePriceFactor <= 0)
      );

      return hasInvalid || this.hasInvalidLinePricingAssignments;
    },
    fixedFilterRules() {
      // Always filter by architecture group
      const architectureGroupFilter = { attributeValue: [this.architectureGroupId] };
      return [Object.assign(this.getArchitectureGroupFilterDefaults(), architectureGroupFilter)];
    },
    isChainsModeEnabled() {
      return this.isFeatureFlagEnabled(this.useStoreGroupChains);
    },
    isManualAnchorEnabled() {
      return (this.releaseFlags && this.releaseFlags.enableManualAnchorInLinePricing) || false;
    },
    // Unique key to persist column state for linePricingEditor
    columnStateStorageKey() {
      return 'linePricingEditor';
    },
    linePricingGroupOptions() {
      return sortBy(
        map(this.linePricingGroups || [], g => ({
          linePricingGroupKey: String(get(g, 'linePricingGroupKey', '')),
          linePricedGroupDescription: String(get(g, 'linePricedGroupDescription', '')),
        })),
        'linePricedGroupDescription'
      );
    },
    hasInvalidLinePricingAssignments() {
      return this.invalidLpgKeys && this.invalidLpgKeys.size > 0;
    },
    sideBar() {
      return {
        toolPanels: [
          this.columnToolPanel,
          {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
          },
        ],
        position: 'right',
        defaultToolPanel: undefined,
        hiddenByDefault: true,
      };
    },
  },

  watch: {
    // Reapply column state when Price List (toolStoreGroupKey) filter changes
    architectureRulesLinePricingFilter: {
      deep: true,
      handler() {
        // Fast-exit: when chains mode is disabled we don't need to reapply
        // price-list driven column transformations.
        if (!this.isChainsModeEnabled) return;

        // If grid isn't ready, rebuild columns; otherwise reuse the
        // centralized applyHideShowColumnState implementation.
        if (!this.gridApi || typeof this.getLinePricingEditorColumnState !== 'function') {
          this.buildColumns();
          return;
        }

        try {
          this.applyHideShowColumnState();
        } catch (e) {
          console.warn('Error applying transformed column state', e);
          this.buildColumns();
        }
      },
    },
    // Recompose rows when groups or candidates change
    linePricingGroups: {
      deep: true,
      handler() {
        this.composeRowData();
      },
    },
    linePricingArchitectureProducts: {
      deep: true,
      handler() {
        this.composeRowData();
      },
    },
    // Re-render prices when norm weight toggle changes
    selectedPriceType() {
      this.composeRowData();
    },
    // Toggle grid loading overlay when store loading state changes
    isLoading(newVal) {
      if (!this.gridApi) return;
      if (newVal) {
        this.gridApi.showLoadingOverlay();
      } else {
        this.gridApi.hideOverlay();
        // Rely on firstDataRendered for initial sizing
      }
    },
    architectureGroupId: {
      async handler(newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          if (Object.keys(this.dirtyRowsByKey).length) {
            this.$set(this, 'dirtyRowsByKey', {});
          }

          await this.initialFetch();

          // Rebuild grid with new data
          this.buildColumns();
          this.composeRowData();
        }
      },
    },
    rowData: {
      deep: true,
      handler() {
        // Improve performance of this one by refreshing only affected columns
        // PRICE-5189 -- consider further optimizations if needed
        this.rebuildInvalidKeysFromRows();
        if (this.gridApi) this.gridApi.refreshCells({ force: true });
      },
    },
  },

  mounted() {
    // Listen for the import finished event
    this.$eventBus.$on('postLinePricingGroupsUpload', this.initialFetch);
  },
  beforeDestroy() {
    // Unsubscribe from the event to prevent memory leaks
    this.$eventBus.$off('postLinePricingGroupsUpload');
  },

  async created() {
    this.gridOptions = {
      tooltipShowDelay: 500,
      suppressMovableColumns: true,
      singleClickEdit: true,
      rowHeight: 30,
      headerHeight: 40,
      suppressMenuHide: true,
      enableFillHandle: true,
      enableRangeSelection: true,
      fillHandleDirection: 'y',
      suppressHorizontalScroll: false,
      statusBar: {
        statusPanels: [
          {
            statusPanel: 'agTotalRowCountComponent',
            align: 'left',
          },
        ],
      },
    };

    // Defer initial fetch and rendering until valid identifiers exist
    await this.initialFetch();
    this.buildColumns();
    this.composeRowData();

    // Take the initial snapshot after first data compose
    this.$nextTick(() => this.refreshOriginalSnapshotIfClean());
  },

  events: {
    // Refetch products when the architecture driver is saved
    // This is to ensure that the products are up to date
    async onArchitectureDriverSaved() {
      await this.initialFetch();

      // Rebuild grid with new data
      this.composeRowData();
    },
  },

  methods: {
    ...mapActions('architectureGroup', [
      'fetchLinePricingProducts',
      'fetchLinePricingGroupsWithProducts',
      'saveLinePricingGroupsWithProducts',
      'createLinePricingGroup',
      'deleteLinePricingGroup',
      'editLinePricingGroup',
    ]),
    ...mapActions('filters', ['setSelectedFilter']),
    ...mapActions('architectureGroup', ['downloadLinePricing']),
    ...mapActions('architectureGroup', ['setLinePricingEditorColumnState']),

    async initialFetch() {
      // Only fetch when we have valid identifiers
      if (!this.architectureGroupId || !this.scenarioKey) return;

      // Apply main banner filter and fetch products
      await this.applyMainBannerFilterAndFetch();
    },

    async applyMainBannerFilterAndFetch() {
      // // Currently we want to load all TSG by default, to show all columns
      // // but keep the code here for future reference, if we decide to limit data per TSGs again.
      // Create main banner filter if available
      // Note: fixedFilterRules are automatically handled by attribute-filter-panel component
      // const storeGroupFilter = this.getMainBannerFilterConfig();
      // const filters = storeGroupFilter ? [storeGroupFilter] : [];

      // // Apply filters to store
      // this.setSelectedFilter({
      //   filterName: 'architectureRulesLinePricingFilter',
      //   filterValue: filters,
      // });

      // Fetch products and groups in parallel with the filter
      await Promise.all([
        this.fetchLinePricingProducts({
          architectureGroupId: this.architectureGroupId,
        }),
        this.fetchLinePricingGroupsWithProducts(),
      ]);
    },

    // Builds grid columns. In chain mode, dynamic per-store-group column groups are added.
    buildColumns() {
      // Get raw saved state from Vuex getter (preserve user preferences)
      const rawSavedState =
        (typeof this.getLinePricingEditorColumnState === 'function' &&
          this.getLinePricingEditorColumnState(this.columnStateStorageKey)) ||
        (this.gridApi ? this.gridApi.getColumnState() : null);
      const previousColumnState = rawSavedState;

      const numericComparator = (valueA, valueB) => {
        return this.formatStringToNumber(valueA) - this.formatStringToNumber(valueB);
      };

      // Base columns (shared for both modes). No pinning.
      const columns = [
        {
          field: 'productKey',
          headerName: this.$t('settings.architecture.productKey'),
          minWidth: 80,
          maxWidth: 150,
          filter: true,
        },
        {
          field: 'description',
          headerName: this.$t('settings.architecture.productDescription'),
          minWidth: 150,
          maxWidth: 300,
          filter: true,
          ...agGridUtils.getTruncatedValueGetters({ truncationLength: 40 }),
        },
        {
          field: 'subGroup',
          headerName: this.$t(
            'settings.setArchitectureRules.architectureDriversRules.productTable.subGroup'
          ),
          minWidth: 50,
          maxWidth: 180,
          filter: true,
        },
        {
          field: 'size',
          headerName: this.$t('settings.architecture.size'),
          minWidth: 50,
          maxWidth: 120,
          filter: true,
        },
        {
          field: 'packageTypeDescription',
          headerName: this.$t('settings.architecture.packageType'),
          minWidth: 50,
          maxWidth: 160,
          filter: true,
        },
      ];
      if (this.isChainsModeEnabled) {
        // Store group chains enabled: append dynamic per-store-group columns grouped by chains
        const chainColumns = this.storeGroupChains.map(chain => ({
          headerName: chain.description,
          groupId: chain._id,
          marryChildren: true,
          children: Object.entries(this.storegroupKeyNameMap)
            .filter(([tsgKey]) => chain.toolStoreGroupKeys.includes(tsgKey))
            .sort(([, descA], [, descB]) => descA.localeCompare(descB))
            .map(([tsgKey, tsgDescription]) => ({
              headerName: tsgDescription,
              groupId: `toolStoreGroup::${tsgKey}`,
              marryChildren: true,
              children: [
                // Add distribution group inside each TSG if feature flag is enabled
                ...(this.isFeatureFlagEnabled(displayDistributionByAssortmentRole)
                  ? [
                      {
                        headerName: this.$t('pricing.mainHeaders.distribution'),
                        marryChildren: true,
                        children: [
                          // Add columns for each assortment role
                          ...Object.values(assortmentRoles).map(role => ({
                            headerName: this.$t(`pricing.assortmentRoles.${role}`),
                            field: `${tsgKey}__distributionByAssortmentRole.${role}.distribution`,
                            colId: `${tsgKey}__distributionByAssortmentRole.${role}`,
                            minWidth: 50,
                            maxWidth: 200,
                            hide: true,
                            filter: true,
                            meta: { tsgKey, subField: `distributionByAssortmentRole.${role}` },
                          })),
                          // Add changing assortment column
                          {
                            headerName: this.$t('pricing.assortmentRoles.changingAssortment'),
                            field: `${tsgKey}__distributionByAssortmentRole.changingAssortment`,
                            colId: `${tsgKey}__distributionByAssortmentRole.changingAssortment`,
                            minWidth: 50,
                            maxWidth: 200,
                            hide: true,
                            filter: true,
                            valueGetter: params => {
                              const distribution = get(
                                params.data,
                                `${tsgKey}__distributionByAssortmentRole`,
                                {}
                              );
                              // changing assortments: furniture holds a changing assortment
                              // Assortment roles in Pricing scopes in https://owlabs.atlassian.net/wiki/spaces/RTLS/pages/4921557031/SOK+FactProductPricingStoreGroup
                              // During tooldata ingestion in analytics/analytics/client_data/databricks_sql_reader.py get_distribution_by_assortment_role
                              // we ingest only the following:
                              // dfa.FurnitureAssortmentRole IN ('N', 'X', 'K', 'M', 'T')
                              // OR (dfa.FurnitureAssortmentRole LIKE 'F%%' AND dfa.ClientFurnitureArchetypeKey LIKE 'V%%')
                              const changingAOKey = Object.keys(distribution).find(key =>
                                key.startsWith('F')
                              );
                              return changingAOKey ? distribution[changingAOKey].distribution : '';
                            },
                          },
                        ],
                      },
                    ]
                  : []),
                {
                  colId: `${tsgKey}__linePricingGroupKey`,
                  field: `${tsgKey}__linePricingGroupKey`,
                  headerName: this.$t('settings.architecture.linePricingGroupTableHeader'),
                  headerComponent: 'LinePricingGroupColumnHeader',
                  headerComponentParams: {
                    tsgKey,
                    onCreate: this.onCreateLinePricingGroup,
                    onEdit: this.onEditLinePricingGroup,
                    onDelete: this.onDeleteLinePricingGroup,
                    label: this.$t('settings.architecture.linePricingGroupTableHeader'),
                  },
                  minWidth: 100,
                  maxWidth: 250,
                  editable: !this.disabled && this.editable,
                  meta: { tsgKey, subField: 'linePricingGroupKey' },
                  cellEditor: 'agRichSelectCellEditor',
                  cellEditorParams: () => ({
                    values: ['', ...this.groupSelectValues.map(o => o.value)],
                  }),
                  cellClassRules: {
                    'cell--invalid-lpg': params =>
                      this.isGroupInvalidForTsg(params.value, params.colDef.meta.tsgKey),
                  },
                  valueFormatter: params => {
                    const key = params.value;
                    if (!key) return '';
                    const option = this.groupSelectValues.find(o => isEqual(o.value, key));
                    return option ? option.label : '';
                  },
                  filterValueGetter: params => {
                    const key = params.data[`${tsgKey}__linePricingGroupKey`];
                    if (!key) return '';
                    const option = this.groupSelectValues.find(o => isEqual(o.value, key));
                    return option ? option.label : '';
                  },
                  filter: true,
                  sortable: true,
                },
                {
                  colId: `${tsgKey}__linePriceFactor`,
                  field: `${tsgKey}__linePriceFactor`,
                  headerName: this.$t('settings.architecture.linePriceFactorTableHeader'),
                  minWidth: 50,
                  maxWidth: 100,
                  editable: !this.disabled && this.editable,
                  meta: { tsgKey, subField: 'linePriceFactor' },
                  valueParser: agGridUtils.getNumericColumnCustomType({
                    formatter: this.formatNumber,
                    formatterParams: { format: numberFormats.priceFormat },
                    stringToNumberFormatter: this.formatStringToNumber,
                  }).valueParser,
                  filter: 'agNumberColumnFilter',
                },
                ...(this.isManualAnchorEnabled
                  ? [
                      {
                        colId: `${tsgKey}__isManualAnchor`,
                        field: `${tsgKey}__isManualAnchor`,
                        headerName: this.$t('settings.architecture.manualAnchorHeader'),
                        minWidth: 120,
                        maxWidth: 160,
                        editable: false,
                        meta: { tsgKey, subField: 'isManualAnchor' },
                        cellRenderer: params =>
                          agGridUtils.utils.checkboxRenderer(
                            { ...params, disabled: this.disabled || !this.editable },
                            () => this.onManualAnchorToggle(params, tsgKey)
                          ),
                        filter: true,
                      },
                    ]
                  : []),
              ],
            })),
        }));

        columns.push(...chainColumns);
      } else {
        // Non-chain: Tool store group should be second when present
        if (this.isFeatureFlagEnabled(this.useZones)) {
          const toolStoreGroupCol = {
            field: 'toolStoreGroupDescription',
            headerName: this.$t('settings.architecture.toolStoreGroup'),
            minWidth: 120,
            maxWidth: 180,
            filter: true,
          };
          columns.splice(1, 0, toolStoreGroupCol);
        }
        // Show cost/live price only in non-chain mode
        columns.push(
          {
            field: 'costPrice',
            headerName: this.$t('settings.architecture.costPrice'),
            comparator: numericComparator,
            minWidth: 50,
            maxWidth: 130,
            filter: 'agNumberColumnFilter',
          },
          {
            field: 'livePrice',
            headerName: this.$t('settings.architecture.livePrice'),
            comparator: numericComparator,
            minWidth: 50,
            maxWidth: 130,
            filter: 'agNumberColumnFilter',
          }
        );
        // Append non-chain specific editable columns
        columns.push(
          {
            field: 'linePricingGroupKey',
            headerName: this.$t('settings.architecture.linePricingGroupTableHeader'),
            headerComponent: 'LinePricingGroupColumnHeader',
            headerComponentParams: {
              tsgKey: null,
              onCreate: this.onCreateLinePricingGroup,
              onEdit: this.onEditLinePricingGroup,
              onDelete: this.onDeleteLinePricingGroup,
              label: this.$t('settings.architecture.linePricingGroupTableHeader'),
            },
            editable: !this.disabled && this.editable,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: () => ({
              values: ['', ...this.groupSelectValues.map(o => o.value)],
            }),
            cellClassRules: {
              'cell--invalid-lpg': params =>
                this.isGroupInvalidForTsg(params.value, params.data.toolStoreGroupKey),
            },
            valueFormatter: params => {
              const key = params.value;
              if (!key) return '';
              const option = this.groupSelectValues.find(o => isEqual(o.value, key));
              return option ? option.label : '';
            },
            filterValueGetter: params => {
              const key = params.data.linePricingGroupKey;
              if (!key) return '';
              const option = this.groupSelectValues.find(o => isEqual(o.value, key));
              return option ? option.label : '';
            },
            minWidth: 180,
            maxWidth: 250,
            filter: true,
            sortable: true,
          },
          {
            field: 'linePriceFactor',
            headerName: this.$t('settings.architecture.linePriceFactorTableHeader'),
            editable: !this.disabled && this.editable,
            valueParser: agGridUtils.getNumericColumnCustomType({
              formatter: this.formatNumber,
              formatterParams: { format: numberFormats.priceFormat },
              stringToNumberFormatter: this.formatStringToNumber,
            }).valueParser,
            minWidth: 100,
            maxWidth: 140,
            filter: 'agNumberColumnFilter',
          },
          ...(this.isManualAnchorEnabled
            ? [
                {
                  field: 'isManualAnchor',
                  headerName: this.$t('settings.architecture.manualAnchorHeader'),
                  editable: false,
                  cellRenderer: params =>
                    agGridUtils.utils.checkboxRenderer(
                      {
                        ...params,
                        disabled:
                          this.disabled || !this.editable || params.data.isDisabledForManualAnchor,
                      },
                      () => this.onManualAnchorToggle(params)
                    ),
                  minWidth: 120,
                  maxWidth: 160,
                  filter: true,
                },
              ]
            : [])
        );
      }

      this.columnDefs = columns.filter(Boolean);
      this.defaultColDef = {
        editable: false,
        resizable: true,
        suppressMovable: true,
        menuTabs: ['filterMenuTab', 'generalMenuTab'],
      };

      // Restore previous column state after defs are applied
      this.$nextTick(() => {
        if (previousColumnState && this.gridApi) {
          try {
            if (typeof this.gridApi.applyColumnState === 'function') {
              this.gridApi.applyColumnState({ state: previousColumnState, applyOrder: true });
            }
          } catch (e) {
            console.warn('Error applying column state', e);
          }
        }
        // Also attempt to apply transformed state according to active Price List filter
        // This ensures the initial render respects a default-selected Price List
        this.applyHideShowColumnState();
      });
    },

    onColumnStateChanged() {
      if (!this.gridApi) return;
      const state = this.gridApi.getColumnState();
      // Persist to Vuex (which persists to localStorage via VuexPersist)
      this.setLinePricingEditorColumnState({
        storageKey: this.columnStateStorageKey,
        columnState: state,
      });
    },

    applyHideShowColumnState() {
      if (!this.gridApi || typeof this.getLinePricingEditorColumnState !== 'function') return;

      const raw =
        this.getLinePricingEditorColumnState(this.columnStateStorageKey) ||
        this.gridApi.getColumnState();

      // Build a map of colId -> meta from the current column definitions (if available)
      let colMetaMap = null;
      try {
        if (this.columnApi && typeof this.columnApi.getAllGridColumns === 'function') {
          const cols = this.columnApi.getAllGridColumns() || [];
          colMetaMap = extractColMetaMapFromColumns(cols);
        }
      } catch (e) {
        colMetaMap = null;
      }

      const transformed = buildHideShowColumnState(
        raw,
        this.architectureRulesLinePricingFilter,
        this.isChainsModeEnabled,
        undefined,
        colMetaMap
      );

      if (typeof this.gridApi.applyColumnState === 'function') {
        try {
          this.gridApi.applyColumnState({ state: transformed, applyOrder: false });
        } catch (e) {
          console.warn('Error applying transformed column state', e);
        }
      }
    },

    // Compose the row dataset for the grid. Two modes:
    // - Chain mode: one row per product with dynamic per-store-group fields
    // - Non-chain mode: one row per product per tool store group
    composeRowData() {
      // Don't compose data if we're still loading to prevent "no rows to show" flash
      if (this.isLoading && this.linePricingArchitectureProducts.length === 0) {
        return;
      }

      // Mark that we've loaded data at least once
      this.hasLoadedData = true;

      if (this.isChainsModeEnabled) {
        // Build per-product rows and collect store group keys from chains
        const rowsByProduct = new Map();
        const allTsgKeys = new Set();

        // Collect all TSG keys from chains to ensure we include all configured TSGs
        this.storeGroupChains.forEach(chain => {
          chain.toolStoreGroupKeys.forEach(tsgKey => allTsgKeys.add(tsgKey));
        });

        this.linePricingArchitectureProducts.forEach(product => {
          const productKeyRaw = product.productKey;
          const tsgKey = product.toolStoreGroupKey;

          let targetRow = rowsByProduct.get(productKeyRaw);
          if (!targetRow) {
            targetRow = {
              id: product._id,
              productKey: product.productKeyDisplay,
              description: product.description,
              subGroup: (get(product, 'subGroups', []) || []).join('-'),
              size: product.size,
              packageTypeDescription: product.packageTypeDescription,
              productKeyRaw,
            };
            rowsByProduct.set(productKeyRaw, targetRow);

            // Initialize all TSG fields for this product (only linePricingGroupKey, not distribution data)
            allTsgKeys.forEach(allTsgKey => {
              if (!(`${allTsgKey}__linePricingGroupKey` in targetRow))
                targetRow[`${allTsgKey}__linePricingGroupKey`] = null;
              if (!(`${allTsgKey}__initLinePricingGroupKey` in targetRow))
                targetRow[`${allTsgKey}__initLinePricingGroupKey`] = null;
            });
          }

          // Set actual values for TSGs that have products
          if (!(`${tsgKey}__linePricingGroupKey` in targetRow))
            targetRow[`${tsgKey}__linePricingGroupKey`] = null;
          if (!(`${tsgKey}__initLinePricingGroupKey` in targetRow))
            targetRow[`${tsgKey}__initLinePricingGroupKey`] =
              targetRow[`${tsgKey}__linePricingGroupKey`];
          if (!(`${tsgKey}__distributionByAssortmentRole` in targetRow))
            targetRow[`${tsgKey}__distributionByAssortmentRole`] = get(
              product,
              'distributionByAssortmentRole'
            );
        });

        // Overlay existing assignments
        (this.linePricingGroups || []).forEach(group => {
          (group.products || []).forEach(p => {
            const row = rowsByProduct.get(p.productKey);
            if (!row) return;
            const tsgKey = p.toolStoreGroupKey;
            if (allTsgKeys.has(tsgKey)) {
              row[`${tsgKey}__linePricingGroupKey`] =
                p.linePricingGroupKey || group.linePricingGroupKey;
              row[`${tsgKey}__linePriceFactor`] = p.linePriceFactor;
              row[`${tsgKey}__isManualAnchor`] = !!p.isManualAnchor;
              row[`${tsgKey}__initLinePricingGroupKey`] =
                row[`${tsgKey}__linePricingGroupKey`] || null;
            }
          });
        });

        this.storeGroupKeys = Array.from(allTsgKeys);
        this.rowData = Array.from(rowsByProduct.values());
        // Rebuild columns to reflect dynamic store groups
        this.buildColumns();

        // Update baseline snapshot only if no pending edits
        this.$nextTick(() => this.refreshOriginalSnapshotIfClean());

        return;
      }

      // Original per-product+TSG rows (store group chains disabled)
      const baseRows = this.linePricingArchitectureProducts.map(product => {
        // Prices already resolved by getPriceFieldObjectPath according to selectedPriceType
        const costValue = this.getIntentionCost(product);
        const liveValue = this.getLivePrice(product);
        return {
          id: product._id,
          productKey: product.productKeyDisplay,
          toolStoreGroupKey: product.toolStoreGroupKey,
          toolStoreGroupDescription: get(this.storegroupKeyNameMap, product.toolStoreGroupKey),
          description: product.description,
          size: product.size,
          packageTypeDescription: product.packageTypeDescription,
          costPrice: this.formatNumber({
            number: costValue,
            format: numberFormats.costFormat,
            nullAsDash: true,
          }),
          livePrice: this.formatNumber({
            number: liveValue,
            format: numberFormats.priceFormat,
            nullAsDash: true,
          }),
          subGroup: (get(product, 'subGroups', []) || []).join('-'),
          // mutable fields
          linePricingGroupKey: null,
          linePriceFactor: 1.0,
          isManualAnchor: false,
          isDisabledForManualAnchor: false,
          productKeyRaw: product.productKey,
          initLinePricingGroupKey: null,
        };
      });

      // Overlay assignments from existing groups
      const productAssignments = {};
      (this.linePricingGroups || []).forEach(group => {
        (group.products || []).forEach(p => {
          const key = `${p.productKey}::${p.toolStoreGroupKey}`;
          productAssignments[key] = {
            linePricingGroupKey: p.linePricingGroupKey || group.linePricingGroupKey,
            linePriceFactor: p.linePriceFactor,
            isManualAnchor: !!p.isManualAnchor,
          };
        });
      });

      baseRows.forEach(row => {
        const key = `${row.productKeyRaw}::${row.toolStoreGroupKey}`;
        if (productAssignments[key]) {
          Object.assign(row, productAssignments[key]);
        }
        // Capture initial assignment snapshot (used for detecting removals)
        row.initLinePricingGroupKey = row.linePricingGroupKey || null;
      });

      // Enforce manual anchor exclusivity per TSG within the same group
      this.applyManualAnchorExclusivity(baseRows);
      this.rowData = baseRows;

      // Update baseline snapshot only if no pending edits
      this.$nextTick(() => this.refreshOriginalSnapshotIfClean());
    },

    // Apply attribute filters: persist in store and refetch data to update grid
    async setAttributeFilter(attributeFilters) {
      // Update the filter state with the new attribute filters
      // Note: main banner filter is already part of the existing filter state if it was set
      await this.setSelectedFilter({
        filterName: 'architectureRulesLinePricingFilter',
        filterValue: attributeFilters,
      });

      // Fetch both datasets in parallel; groups don't depend on attribute filters
      await Promise.all([
        this.fetchLinePricingProducts({
          architectureGroupId: this.architectureGroupId,
          where: attributeFilters,
        }),
        this.fetchLinePricingGroupsWithProducts(),
      ]);
      this.composeRowData();
    },

    // Ensures only one manual anchor per tool store group within the same pricing group
    applyManualAnchorExclusivity(rows) {
      const grouped = groupBy(
        rows,
        r => `${r.toolStoreGroupKey}::${r.linePricingGroupKey || 'none'}`
      );
      Object.values(grouped).forEach(groupRows => {
        const anchor = find(groupRows, r => r.isManualAnchor);
        groupRows.forEach(r => {
          r.isDisabledForManualAnchor = !!anchor && r !== anchor;
        });
      });
    },

    onManualAnchorToggle(params, tsgKey = null) {
      if (!this.isManualAnchorEnabled) return;

      const row = params.data;
      const isChainMode = this.isChainsModeEnabled;

      // Determine field name based on mode
      const field = isChainMode && tsgKey ? `${tsgKey}__isManualAnchor` : 'isManualAnchor';

      // Toggle the value
      row[field] = !row[field];

      // Mark row as dirty (different methods for different modes)
      if (isChainMode && tsgKey) {
        this.markRowDirty(row, tsgKey);
      } else {
        this.markRowDirty(row);
        // Apply exclusivity rules only in non-chain mode
        this.applyManualAnchorExclusivity(this.rowData);
      }

      // Refresh grid cells
      if (params && params.node) {
        agGridUtils.refreshGridCells(this.gridApi, params.node, field);
      }
    },

    fillOperation(params) {
      if (!this.fillSourceSubGroups || params.currentIndex === 0) {
        this.fillSourceSubGroups = [];
        const cellRanges = params.api.getCellRanges();
        if (cellRanges && cellRanges.length > 0) {
          const range = cellRanges[0];
          const startRow = Math.min(range.startRow.rowIndex, range.endRow.rowIndex);
          const endRow = Math.max(range.startRow.rowIndex, range.endRow.rowIndex);

          for (let i = startRow; i <= endRow; i += 1) {
            const node = params.api.getDisplayedRowAtIndex(i);
            if (node && node.data) {
              this.fillSourceSubGroups.push(node.data.subGroup);
            }
          }
        }
      }

      const result = handleLinePricingGroupFillOperation(params, this.fillSourceSubGroups);

      if (!result.shouldFill) {
        return false;
      }

      const currentRow = params.rowNode.data;

      if (result.scalingFactorUpdate) {
        currentRow[result.scalingFactorUpdate.field] = result.scalingFactorUpdate.value;
      }

      if (result.tsgKey) {
        this.markRowDirty(currentRow, result.tsgKey);
      } else {
        this.markRowDirty(currentRow);
      }

      this.$nextTick(() => {
        if (this.gridApi && result.scalingFactorUpdate) {
          this.gridApi.refreshCells({
            rowNodes: [params.rowNode],
            columns: [params.column.colDef.field, result.scalingFactorUpdate.field],
            force: true,
          });
        }
      });

      return result.fillValue;
    },

    onCellValueChanged(params) {
      if (!params || !params.data) return;
      const field = params.colDef.field;
      if (!field) return;

      const result = handleLinePricingGroupValueChange(params);

      if (result) {
        params.data[result.linePriceFactorField] = result.nextFactorValue;

        this.$nextTick(() => {
          if (this.gridApi) {
            this.gridApi.refreshCells({
              rowNodes: [params.node],
              columns: [result.linePriceFactorField],
              force: true,
            });
          }
        });
      }

      // Mark row as dirty for relevant field changes
      if (this.isChainsModeEnabled && field.includes('__')) {
        const [tsgKey, subField] = field.split('__');
        if (['linePricingGroupKey', 'linePriceFactor', 'isManualAnchor'].includes(subField)) {
          this.markRowDirty(params.data, tsgKey);
        }
      } else if (['linePricingGroupKey', 'linePriceFactor'].includes(field)) {
        this.markRowDirty(params.data);
      }
    },

    // Track edits in an object keyed by productKeyRaw and toolStoreGroupKey
    markRowDirty(row, tsgKey = null) {
      const isChainMode = this.isChainsModeEnabled;

      // Determine tool store group key based on mode
      const toolStoreGroupKey = isChainMode && tsgKey ? tsgKey : row.toolStoreGroupKey;

      // Create the key for tracking dirty rows
      const key = `${row.productKeyRaw}::${toolStoreGroupKey}`;

      // Determine field access pattern based on mode
      const getFieldValue = fieldName => {
        if (isChainMode && tsgKey) {
          return row[`${tsgKey}__${fieldName}`];
        }
        return row[fieldName];
      };

      const initialKeyField =
        isChainMode && tsgKey ? `${tsgKey}__initLinePricingGroupKey` : 'initLinePricingGroupKey';

      const currentLinePricingGroupKey = getFieldValue('linePricingGroupKey') || null;
      const initialLinePricingGroupKey = row[initialKeyField] || null;

      // If now empty and was empty originally, remove any stale dirty state
      if (!currentLinePricingGroupKey && !initialLinePricingGroupKey) {
        if (this.dirtyRowsByKey[key]) {
          this.$delete(this.dirtyRowsByKey, key);
        }
        return;
      }

      this.$set(this.dirtyRowsByKey, key, {
        productKey: row.productKeyRaw,
        productKeyDisplay: row.productKey,
        toolStoreGroupKey,
        scenarioKey: this.scenarioKey,
        linePricingGroupKey: currentLinePricingGroupKey,
        initialLinePricingGroupKey,
        linePriceFactor: Number(getFieldValue('linePriceFactor')),
        isManualAnchor: !!getFieldValue('isManualAnchor'),
      });
    },

    // formatNumber and formatStringToNumber provided by formatNumber mixin

    getLivePrice(item) {
      const field = this.getPriceFieldObjectPath('livePrice');
      return get(item, field);
    },

    getIntentionCost(item) {
      const field = this.getPriceFieldObjectPath('intentionCost');
      return get(item, field);
    },

    getMainBannerFilterConfig() {
      if (!this.mainToolStoreGroupKeys || this.mainToolStoreGroupKeys.length === 0) {
        return null;
      }

      return {
        attributeKey: 'toolStoreGroupKey',
        attributeFilterType: storegroupOption,
        predicate: 'IN',
        attributeValue: this.mainToolStoreGroupKeys,
        displayValue: null,
        attributeDescription: this.$t('pricing.toolStoreGroup'),
        displayDescription: this.$t('pricing.toolStoreGroup'),
      };
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;

      // Show loading overlay only if we're still loading OR haven't loaded data yet
      // Don't show loading if we've loaded data but it's empty (let grid show "no rows to show")
      if (this.isLoading || !this.hasLoadedData) {
        this.gridApi.showLoadingOverlay();
      }
    },

    onFirstDataRendered() {
      if (this.gridApi) {
        this.gridApi.autoSizeColumns();
      }
    },

    // Refresh initial snapshots after data load or save to ensure clears work correctly
    refreshInitialSnapshots() {
      if (!this.rowData || !this.rowData.length) return;

      (this.rowData || []).forEach(row => {
        if (this.isChainsModeEnabled) {
          this.storeGroupKeys.forEach(tsgKey => {
            row[`${tsgKey}__initLinePricingGroupKey`] =
              row[`${tsgKey}__linePricingGroupKey`] || null;
          });
        } else {
          row.initLinePricingGroupKey = row.linePricingGroupKey || null;
        }
      });
    },

    // Group edited rows by linePricingGroupKey to match backend API shape
    onSave() {
      if (this.isSaveDisabled) return;
      this.isSaving = true;

      // Build linePricingGroupsToUpdate payload grouped by linePricingGroupKey
      const updatesByGroup = {};
      const removals = [];

      Object.values(this.dirtyRowsByKey).forEach(r => {
        if (r.linePricingGroupKey) {
          const arr =
            updatesByGroup[r.linePricingGroupKey] || (updatesByGroup[r.linePricingGroupKey] = []);
          arr.push({
            productKey: r.productKey,
            productKeyDisplay: r.productKeyDisplay,
            toolStoreGroupKey: r.toolStoreGroupKey,
            scenarioKey: r.scenarioKey,
            architectureGroupId: this.architectureGroupId,
            linePricingGroupKey: r.linePricingGroupKey,
            linePriceFactor: r.linePriceFactor,
            isManualAnchor: r.isManualAnchor,
          });
        } else if (!r.linePricingGroupKey && r.initialLinePricingGroupKey) {
          removals.push({
            productKey: r.productKey,
            toolStoreGroupKey: r.toolStoreGroupKey,
            scenarioKey: r.scenarioKey,
            architectureGroupId: this.architectureGroupId,
            linePricingGroupKey: r.initialLinePricingGroupKey,
          });
        }
      });

      const linePricingGroupsToUpdate = Object.keys(updatesByGroup).map(linePricingGroupKey => ({
        linePricingGroupKey,
        scenarioKey: this.scenarioKey,
        architectureGroupId: this.architectureGroupId,
        products: updatesByGroup[linePricingGroupKey],
      }));

      const payload = {
        linePricingGroupsToUpdate,
        linePricingGroupsToRemove: removals,
      };

      this.saveLinePricingGroupsWithProducts({ linePricingGroups: payload })
        .then(() => {
          this.$set(this, 'dirtyRowsByKey', {});
          this.refreshInitialSnapshots();

          // Refresh the full-rows snapshot so future Cancel returns to this post-save state
          // Consider in future to implement more granular snapshot updates to avoid full clone
          // There is already something similar in inputs page
          this.originalRowsSnapshot = cloneDeep(this.rowData);
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    onImportClick() {
      this.importDialog = true;
    },

    onDownloadClick() {
      const toolStoreGroupTranslation = this.isFeatureFlagEnabled(this.useZones)
        ? {
            toolStoreGroupDescription: this.$t('pricing.toolStoreGroup'),
          }
        : {};
      const translationMap = {
        productKeyDisplay: this.$t('pricing.productKey'),
        productName: this.$t('pricing.productDescription'),
        ...toolStoreGroupTranslation,
        category: this.$t('pricing.category'),
        pricingGroup: this.$t('pricing.pricingGroup'),
        architectureGroup: this.$t('pricing.architectureGroup'),
        linePricedGroupDescription: this.$t('settings.architecture.linePricingGroupHeader'),
        linePriceFactor: this.$t('settings.architecture.LinepriceFactorHeader'),
      };
      this.downloadLinePricing({
        translationMap,
        architectureGroupId: this.architectureGroupId,
      });
    },
    onCreateLinePricingGroup(tsgKey) {
      this.currentTsgKey = tsgKey || null;
      this.lpgDialogMode = 'create';
      this.lpgDialogSuccess = false;
      this.showLpgDialog = true;
    },
    onEditLinePricingGroup(tsgKey) {
      this.currentTsgKey = tsgKey || null;
      this.lpgDialogMode = 'edit';
      this.lpgDialogSuccess = false;
      this.showLpgDialog = true;
    },
    onDeleteLinePricingGroup(tsgKey) {
      this.currentTsgKey = tsgKey || null;
      this.lpgDialogMode = 'delete';
      this.lpgDialogSuccess = false;
      this.showLpgDialog = true;
    },
    async onDialogCreate({ name }) {
      if (!name) return;
      try {
        this.lpgDialogSubmitting = true;
        await this.createLinePricingGroup({
          linePricedGroupDescription: name,
          architectureGroupId: this.architectureGroupId,
          scenarioKey: this.scenarioKey,
        });

        this.lpgDialogSuccess = true;
        this.$store.dispatch('notifications/addNotification', {
          content: this.$t(
            'settings.architecture.linePricingGroups.dialog.linePricingGroupCreated'
          ),
          color: 'success',
        });
      } catch (e) {
        const duplicate = e && (e.code === 409 || (e.response && e.response.status === 409));
        this.$store.dispatch('notifications/addNotification', {
          content: duplicate
            ? this.$t('settings.architecture.linePricingGroups.dialog.linePricingGroupNameExists')
            : this.$t(
                'settings.architecture.linePricingGroups.dialog.createLinePricingGroupFailed'
              ),
          color: 'error',
        });
        // Keep dialog open in form state; input stays red due to rules
      } finally {
        this.lpgDialogSubmitting = false;
      }
    },

    async onDialogEdit({ linePricingGroupKey, name }) {
      if (!linePricingGroupKey || !name) return;
      try {
        this.lpgDialogSubmitting = true;
        await this.editLinePricingGroup({
          linePricingGroupKey,
          linePricedGroupDescription: name,
        });

        this.lpgDialogSuccess = true;
        this.$store.dispatch('notifications/addNotification', {
          content: this.$t(
            'settings.architecture.linePricingGroups.dialog.linePricingGroupUpdated'
          ),
          color: 'success',
        });
      } catch (e) {
        const duplicate = e && (e.code === 409 || (e.response && e.response.status === 409));
        this.$store.dispatch('notifications/addNotification', {
          content: duplicate
            ? this.$t('settings.architecture.linePricingGroups.dialog.linePricingGroupNameExists')
            : this.$t(
                'settings.architecture.linePricingGroups.dialog.updateLinePricingGroupFailed'
              ),
          color: 'error',
        });
      } finally {
        this.lpgDialogSubmitting = false;
      }
    },

    async onDialogDelete({ linePricingGroupKey }) {
      if (!linePricingGroupKey) return;
      try {
        this.lpgDialogSubmitting = true;

        await this.deleteLinePricingGroup(linePricingGroupKey);

        this.lpgDialogSuccess = true;

        this.$store.dispatch('notifications/addNotification', {
          content: this.$t(
            'settings.architecture.linePricingGroups.dialog.linePricingGroupDeleted'
          ),
          color: 'success',
        });
      } catch (e) {
        this.$store.dispatch('notifications/addNotification', {
          content: this.$t(
            'settings.architecture.linePricingGroups.dialog.deleteLinePricingGroupFailed'
          ),
          color: 'error',
        });
      } finally {
        this.lpgDialogSubmitting = false;
      }
    },

    onCloseLpgDialog() {
      this.showLpgDialog = false;
      this.lpgDialogSuccess = false;
      this.currentTsgKey = null;
    },

    onDismissLpgDialog() {
      this.showLpgDialog = false;
      this.lpgDialogSuccess = false;
      this.currentTsgKey = null;

      // Refresh grid visuals (rows will be recomposed by watchers)
      if (this.gridApi) {
        this.gridApi.refreshHeader();
        this.gridApi.refreshCells();
      }
    },

    handleImportClick({ importAG }) {
      this.importByArchitectureGroup = importAG;
      this.showLPGUpload = true;
    },

    refreshOriginalSnapshotIfClean() {
      if (Object.keys(this.dirtyRowsByKey).length) return;
      if (Array.isArray(this.rowData)) {
        // Consider in future to implement more granular snapshot updates to avoid full clone
        // There is already something similar in inputs page
        this.originalRowsSnapshot = cloneDeep(this.rowData);
      }
    },

    onDiscard() {
      this.discardChanges();
    },

    discardChanges() {
      if (!this.gridApi) return;

      // Stop any active editing session to avoid partial editor states
      this.gridApi.stopEditing();

      if (Array.isArray(this.originalRowsSnapshot)) {
        // Replace bound rowData; AG Grid will re-render
        this.rowData = cloneDeep(this.originalRowsSnapshot);
      }

      // Clear all dirty state
      this.$set(this, 'dirtyRowsByKey', {});

      // Refresh visuals
      this.$nextTick(() => {
        try {
          this.gridApi.refreshCells({ force: true });
          this.gridApi.redrawRows();
        } catch (e) {
          console.warn('Error refreshing grid after discard', e);
        }
      });
    },

    rebuildInvalidKeysFromRows() {
      // Map of "<linePricingGroupKey>::<toolStoreGroupKey>" -> Set of distinct product subgroups
      const subgroupsByAssignmentKey = Object.create(null);

      const addSubgroupToAssignment = (linePricingGroupKey, toolStoreGroupKey, productSubgroup) => {
        if (!linePricingGroupKey || !toolStoreGroupKey || !productSubgroup) return;
        const assignmentKey = `${linePricingGroupKey}::${toolStoreGroupKey}`;
        (
          subgroupsByAssignmentKey[assignmentKey] ||
          (subgroupsByAssignmentKey[assignmentKey] = new Set())
        ).add(productSubgroup);
      };

      const rows = Array.isArray(this.rowData) ? this.rowData : [];

      if (this.isChainsModeEnabled) {
        const toolStoreGroupKeys = this.storeGroupKeys || [];
        rows.forEach(row => {
          const productSubgroup = row.subGroup;
          if (!productSubgroup) return;

          toolStoreGroupKeys.forEach(toolStoreGroupKey => {
            const linePricingGroupKey = row[`${toolStoreGroupKey}__linePricingGroupKey`];
            if (linePricingGroupKey) {
              addSubgroupToAssignment(linePricingGroupKey, toolStoreGroupKey, productSubgroup);
            }
          });
        });
      } else {
        rows.forEach(row =>
          addSubgroupToAssignment(row.linePricingGroupKey, row.toolStoreGroupKey, row.subGroup)
        );
      }

      // Any assignment with >1 subgroup is invalid
      const invalidAssignmentKeys = new Set();
      Object.keys(subgroupsByAssignmentKey).forEach(assignmentKey => {
        if (subgroupsByAssignmentKey[assignmentKey].size > 1) {
          invalidAssignmentKeys.add(assignmentKey);
        }
      });

      this.invalidLpgKeys = invalidAssignmentKeys;
    },

    isGroupInvalidForTsg(lpgKey, tsgKey) {
      if (!lpgKey || !tsgKey) return false;
      return this.invalidLpgKeys.has(`${lpgKey}::${tsgKey}`);
    },

    isRowInInvalidAssignment(row) {
      if (this.isChainsModeEnabled) {
        return (this.storeGroupKeys || []).some(tsg => {
          const lpg = row[`${tsg}__linePricingGroupKey`];
          return lpg && this.isGroupInvalidForTsg(lpg, tsg);
        });
      }
      return this.isGroupInvalidForTsg(row.linePricingGroupKey, row.toolStoreGroupKey);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.ag-grid-box {
  height: 60rem;
  width: 100%;
}

.filter-by-text {
  overflow: hidden;
  color: var(--text-icon-color-text-primary, #000000de);
  text-overflow: ellipsis;
  font-family: 'Noto Sans';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
}

.filter-buttons-container {
  margin-right: 8px;
}

.columns-toggle,
.filters-toggle {
  width: auto;
  height: 28px;
}

.grid-btn {
  letter-spacing: normal;
}

.alert-message.v-alert {
  border: none !important;
  font-size: 1.25rem;
  height: 26px;
}

// AG Grid column separators
::v-deep {
  .ag-theme-custom-stripped {
    // Remove all spacing and gaps
    .ag-root-wrapper {
      border-spacing: 0;
    }

    // Header group cells - use AG Grid default approach
    .ag-header-group-cell:not(:first-child) {
      border-left: 1px solid $custom-ag-grid-header-separator-color;
    }

    // Individual header cells - use AG Grid default approach
    .ag-header-cell:not(:first-child) {
      border-left: 1px solid $custom-ag-grid-header-separator-color;
    }

    // Make resize handles white (invisible but functional)
    .ag-header-cell-resize::after,
    .ag-header-group-cell .ag-header-cell-resize::after {
      background-color: white !important;
    }

    // Data rows
    .ag-row {
      border-bottom: none;
      border-top: none;
    }

    // Data cells - use AG Grid default approach: border on all cells except first
    .ag-cell:not(:first-child) {
      border-left: 1px solid $custom-ag-grid-header-separator-color;
    }

    .cell--invalid-lpg {
      border: 1px solid #d32f2f !important;
      border-radius: 2px;
      box-sizing: border-box;
    }
  }
}
</style>
